import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCopilotUnresolvedAPI } from "../../utils/api/copilotAdmin";

// Unresolved (open + pending) conversations for the sidebar badge. The number
// always comes from the backend, never from a loaded list.
//
//   enabled   false for roles without Copilot access: no request is ever made.
//
// Freshness without any new infrastructure:
//   - one request on mount, then a light poll while the page is visible,
//   - a refresh when the tab becomes visible/focused again (throttled),
//   - an immediate refresh when the inbox changes a conversation
//     (announceCopilotChange), so resolving/reopening updates the badge at once.

export const COPILOT_COUNT_EVENT = "lud-copilot-unresolved-change";
export const COPILOT_POLL_MS = 45000;
const FOCUS_MIN_MS = 10000;

// Called by the inbox after any change; every mounted badge re-fetches.
export function announceCopilotChange() {
  window.dispatchEvent(new CustomEvent(COPILOT_COUNT_EVENT));
}

export function useCopilotUnresolved(enabled) {
  const [count, setCount] = useState(0);
  const inFlight = useRef(false);
  const last = useRef(0);
  const latest = useRef(0);

  const load = useCallback(async () => {
    if (!enabled || inFlight.current || !localStorage.getItem("auth-token")) return;
    inFlight.current = true;
    const ticket = (latest.current += 1);
    last.current = Date.now();
    try {
      const data = await fetchCopilotUnresolvedAPI();
      // A newer request started meanwhile: let that one win.
      if (ticket === latest.current) setCount(Math.max(0, Number(data.unresolved) || 0));
    } catch {
      // keep the last known number; the next poll retries
    } finally {
      inFlight.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, COPILOT_POLL_MS);
    const onReturn = () => {
      if (document.visibilityState === "visible" && Date.now() - last.current > FOCUS_MIN_MS) load();
    };
    const onChange = () => {
      inFlight.current = false; // a change is newer than any request in flight
      load();
    };
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    window.addEventListener(COPILOT_COUNT_EVENT, onChange);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
      window.removeEventListener(COPILOT_COUNT_EVENT, onChange);
    };
  }, [enabled, load]);

  return count;
}
